import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, Clock, FileText, Newspaper, Sparkles, Activity, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const BORDER = "#E7E5E2";
const ICON_MAP = { Radio, Clock, FileText, Newspaper, Sparkles };

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 0 || Number.isNaN(diff)) return new Date(iso).toLocaleDateString("pt-BR");
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s atrás`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min atrás`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h atrás`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d atrás`;
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch { return ""; }
}

const KIND_ACCENT = {
  djen:     { color: "#0284c7", bg: "#e0f2fe" },
  deadline: { color: "#E11D48", bg: "#ffe4e6" },
  document: { color: "#059669", bg: "#d1fae5" },
  news:     { color: GOLD,      bg: "#FBE7A6" },
};

const LS_KEY = "hermes.activity.last_seen";

export default function HermesActivity({ limit = 8 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get(`/dashboard/activity?limit=${limit}`);
        if (mounted) setItems(r.data?.items || []);
      } catch { /* silent */ }
      finally { if (mounted) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 45000); // atualiza a cada 45s
    const tv = setInterval(() => setTick(t => t + 1), 30000); // atualiza tempos relativos
    return () => { mounted = false; clearInterval(iv); clearInterval(tv); };
    // eslint-disable-next-line
  }, [limit]);

  // Conta eventos novos (at > lastSeen)
  const newCount = items.reduce((acc, ev) => {
    if (lastSeen && ev.at && String(ev.at) > String(lastSeen)) return acc + 1;
    if (!lastSeen && ev.at) return acc + 1;
    return acc;
  }, 0);

  const markAllSeen = () => {
    const newest = items[0]?.at || new Date().toISOString();
    try { localStorage.setItem(LS_KEY, newest); } catch {}
    setLastSeen(newest);
  };

  return (
    <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }}
         data-testid="hermes-activity">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative w-7 h-7 rounded-lg flex items-center justify-center"
               style={{ background: `${NAVY}`, color: "#D2A85A" }}>
            <Activity className="w-3.5 h-3.5"/>
            {newCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white"
                    data-testid="activity-new-badge">
                {newCount > 99 ? "99+" : newCount}
              </span>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">HERMES · TIMELINE</div>
            <div className="font-serif text-base text-[#132238] leading-tight">Atividade recente</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {newCount > 0 && (
            <button
              onClick={markAllSeen}
              data-testid="activity-mark-seen"
              className="text-[10px] text-slate-500 hover:text-[#132238] underline decoration-dotted">
              Marcar como visto
            </button>
          )}
          <span className="text-[10px] text-slate-400 flex items-center gap-1"
                title="Atualiza automaticamente a cada 45s">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/> ao vivo
          </span>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-xs text-slate-400 py-8 text-center" data-testid="activity-loading">
          Carregando timeline…
        </div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-400 py-8 text-center" data-testid="activity-empty">
          Sem eventos recentes — o HERMES vai reportar aqui as próximas ações.
        </div>
      ) : (
        <ul className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1" data-testid="activity-list">
          {items.map((ev, i) => {
            const Icon = ICON_MAP[ev.icon] || Sparkles;
            const accent = KIND_ACCENT[ev.kind] || { color: NAVY, bg: "#f1f5f9" };
            const isNew = lastSeen && ev.at && String(ev.at) > String(lastSeen);
            const inner = (
              <li className="flex gap-3 group relative" data-testid={`activity-item-${ev.kind}-${i}`}>
                {isNew && (
                  <span className="absolute left-6 top-0 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white"
                        aria-label="Novo"/>
                )}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: accent.bg, color: accent.color }}>
                  <Icon className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="text-[12.5px] text-[#132238] leading-snug truncate flex-1">
                      {ev.title}
                    </div>
                    {isNew && (
                      <span className="text-[8.5px] font-bold tracking-widest px-1 py-0.5 rounded bg-rose-500 text-white">
                        NOVO
                      </span>
                    )}
                  </div>
                  {ev.detail && (
                    <div className="text-[11px] text-slate-500 truncate">
                      {ev.detail}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-400">
                      {relativeTime(ev.at)}
                    </span>
                    {ev.context && (
                      <span className="text-[10px] text-slate-400 font-mono">
                        · {ev.context}
                      </span>
                    )}
                  </div>
                </div>
                {ev.link && (
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400 self-center opacity-0 group-hover:opacity-100 transition"/>
                )}
              </li>
            );
            return ev.link ? (
              <Link key={i} to={ev.link} className="block hover:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition">
                {inner}
              </Link>
            ) : (
              <div key={i}>{inner}</div>
            );
          })}
        </ul>
      )}
    </div>
  );
}
